import { useState } from "react";
import { Tooltip as TooltipPrimitive } from "radix-ui";
import { InfoIcon } from "lucide-react";

// The repo-wide tooltip convention: an always-visible "i" (native `title` gives no affordance and misses keyboard users).
// Sandbox keeps a copy at apps/sandbox/src/sdk/info-hint.tsx (its SDK must be self-contained): keep the two in step.

export interface InfoHintProps {
  /** The explanation. Keep it to a sentence or two - anything longer belongs on the page. */
  children: React.ReactNode;
  /** Names what is being explained, for screen readers: "More about Automated". */
  label: string;
  /** Which side the bubble prefers. Defaults to top. */
  side?: "top" | "right" | "bottom" | "left";
  className?: string;
}

export function InfoHint({ children, label, side = "top", className = "" }: InfoHintProps) {
  // Controlled so a tap opens it too: Radix tooltips are pointer- and focus-driven, which leaves touch
  // users with no way in.
  const [open, setOpen] = useState(false);

  return (
    <TooltipPrimitive.Provider delayDuration={150}>
      <TooltipPrimitive.Root open={open} onOpenChange={setOpen}>
        <TooltipPrimitive.Trigger asChild>
          <button
            type="button"
            aria-label={`More about ${label}`}
            onClick={(e) => {
              e.stopPropagation();
              setOpen((v) => !v);
            }}
            className={`inline-flex shrink-0 cursor-help items-center justify-center rounded-full text-muted-foreground/60 transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 ${className}`}
          >
            <InfoIcon className="size-3.5" aria-hidden />
          </button>
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={4}
            collisionPadding={8}
            className="z-50 max-w-xs origin-(--radix-tooltip-content-transform-origin) animate-in rounded-md bg-foreground px-3 py-1.5 text-left text-xs leading-snug text-balance text-background fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
          >
            {children}
            <TooltipPrimitive.Arrow className="z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-foreground fill-foreground" />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
